import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { FormProvider } from 'core/states';
import { useAdminUserActions } from 'pricing/hooks';
import { IPricingProfileAdminUser } from 'pricing/types';

import { upsertPricingProfileAdminUserSchema } from './schema';
import { IPricingProfileAdminUserForm } from './types';
import { UpsertPricingProfileAdminUserFormFields } from './UpsertPricingProfileAdminUserFormFields';
import { mapUpsertPricingProfileAdminUser } from './utils';

interface IProps {
  pricingProfileAdminUser?: IPricingProfileAdminUser;
  pricingProfileId: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
}

export const UpsertPricingProfileAdminUser = ({
  pricingProfileAdminUser,
  pricingProfileId,
  isOpen,
  onClose,
  onSubmitSuccess,
}: IProps) => {
  const { upsertAdminUser } = useAdminUserActions();

  const defaultValues = useMemo(() => {
    return {
      pricingProfileId,
      corporateUser: pricingProfileAdminUser
        ? {
            id: pricingProfileAdminUser?.corporateUser.id,
            name: pricingProfileAdminUser?.corporateUser.name,
          }
        : null,
    };
  }, [pricingProfileAdminUser, pricingProfileId]);

  const onSubmit =
    (reset: UseFormReset<IPricingProfileAdminUserForm>) =>
    async (data: IPricingProfileAdminUserForm) => {
      const { success } = await upsertAdminUser(mapUpsertPricingProfileAdminUser(data));

      if (!success) {
        return;
      }

      setTimeout(() => {
        onSubmitSuccess();
        handleOnClose(reset);
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<IPricingProfileAdminUserForm>) => {
    onClose();
    reset?.(defaultValues);
  };

  return (
    <FormProvider
      schema={upsertPricingProfileAdminUserSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertPricingProfileAdminUserFormFields
        pricingProfileAdminUser={pricingProfileAdminUser}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
